import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

/**
 * Prominent "return to where you came from" affordance for detail views.
 *
 * By default uses router history (navigate(-1)) so it restores the previous
 * screen — deposit ledger, search results, customer list, wherever the user
 * arrived from. Originating screens can pass `state.fromLabel` when navigating
 * to make the label specific (e.g. "Back to deposit ledger").
 *
 * Callers with a known destination can override `label` + `onClick` (e.g. the
 * calendar deep-link) so every detail view shares one visible back control.
 */
export default function BackLink({
  fallbackTo = '/',
  className = '',
  label,
  onClick,
}: {
  fallbackTo?: string;
  className?: string;
  label?: string;
  onClick?: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const fromLabel = (location.state as { fromLabel?: string } | null)?.fromLabel;
  const text = label ?? (fromLabel ? `Back to ${fromLabel}` : 'Back');

  const handleClick =
    onClick ??
    (() => {
      if (window.history.length > 1) navigate(-1);
      else navigate(fallbackTo);
    });

  return (
    <div className={className}>
      <button
        type="button"
        onClick={handleClick}
        className="inline-flex items-center gap-1.5 rounded-md border border-gold-dim/50 bg-ink-deep/60 pl-2 pr-3.5 py-2 text-sm font-medium text-cream shadow-ink-soft hover:border-gold-bright hover:text-gold-bright hover:bg-gold/10 transition"
        data-testid="back-link"
      >
        <ChevronLeft size={18} className="shrink-0" /> {text}
      </button>
    </div>
  );
}
